/*
 * PhoneTabs: the bar at the foot of the console in its phone layout
 * (usePhoneLayout), holding the places people go on a phone, and More, which
 * opens every screen as a sheet: the sidebar's own navigation, so nothing the
 * sidebar offers is out of reach.
 *
 * Sites is the fleet, which a site's own screens open from; Needs you is what
 * waits for this person's yes; Alerts what the Weavian server judges wrong
 * right now; Interruptions where the picked site's connections broke. A place
 * the picked box or this build does not offer is left out, as the sidebar
 * leaves out a screen, and on a box's own console the first place is its
 * dashboard. Needs you and Alerts carry their count: amber for a person's
 * yes, red for something wrong, the console's meanings for those colours.
 *
 * ! file components/PhoneTabs.js
 */
import React, { useCallback, useState } from "react";
import { FiInbox, FiMenu } from "react-icons/fi";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { navIcon, viewOffered } from "../navigation";
import { NEEDS_YOU } from "./approvalWords";
import "./PhoneTabs.css";

const ALERTS_MS = 30000;
const MORE = "more";

const PLACES = [
  { key: "nodes", label: "Sites" },
  { key: "dashboard", label: "Dashboard" },
  { key: "approval", label: NEEDS_YOU, icon: FiInbox, count: (k) => `${k} waiting for your yes` },
  { key: "alerts", label: "Alerts", count: (k) => `${k} open now` },
  { key: "continuity", label: "Interruptions" },
];

/* The places one edge offers on this build: the dashboard only where it is
   home, on the box's own console, and Needs you only where a server asks. */
export function phonePlaces({ mode, singleEdge, phone, server }) {
  return PLACES.filter((p) => (p.key === "dashboard" ? singleEdge
    : p.key === "approval" ? server
      : viewOffered(p.key, { mode, singleEdge, phone })));
}

/* The place a screen is lit under: its own, a site's dashboard under Sites,
   and any other under More, where it was opened from. */
export function placeOf(places, view) {
  if (places.some((p) => p.key === view)) return view;
  return view === "dashboard" && places.some((p) => p.key === "nodes") ? "nodes" : MORE;
}

/* How many alerts are open now, as the Alerts screen reads them. */
function useOpenAlerts(serverApiUrl, enabled) {
  const [open, setOpen] = useState(0);
  const read = useCallback(async () => {
    const res = await fetchWithTimeout(serverApiUrl("/alerts"), { credentials: "same-origin" }).catch(() => null);
    const body = res?.ok ? await res.json().catch(() => null) : null;
    if (Array.isArray(body?.active)) setOpen(body.active.length);
  }, [serverApiUrl]);
  useVisibleInterval(read, ALERTS_MS, enabled);
  return open;
}

export default function PhoneTabs({ places, view, moreOpen, onGo, onMore, serverApiUrl, waiting = 0 }) {
  const alerts = useOpenAlerts(serverApiUrl, places.some((p) => p.key === "alerts"));
  const lit = moreOpen ? MORE : placeOf(places, view);
  const counts = { approval: waiting, alerts };
  return (
    <nav className="phone-tabs" aria-label="Places">
      {places.map((p) => {
        const Icon = p.icon || navIcon(p.key);
        const k = counts[p.key] || 0;
        return (
          <button
            key={p.key}
            type="button"
            className={`phone-tab${lit === p.key ? " is-on" : ""}`}
            aria-current={lit === p.key ? "page" : undefined}
            aria-label={k ? `${p.label}, ${p.count(k)}` : undefined}
            onClick={() => onGo(p.key)}
          >
            <span className="phone-tab-icon">
              <Icon size={21} aria-hidden="true" />
              {k > 0 && <span className={`phone-tab-count ${p.key === "approval" ? "is-wait" : "is-bad"}`} aria-hidden="true">{k}</span>}
            </span>
            <span className="phone-tab-label">{p.label}</span>
          </button>
        );
      })}
      <button type="button" className={`phone-tab${lit === MORE ? " is-on" : ""}`} aria-expanded={moreOpen} onClick={onMore}>
        <span className="phone-tab-icon"><FiMenu size={21} aria-hidden="true" /></span>
        <span className="phone-tab-label">More</span>
      </button>
    </nav>
  );
}
